"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiArrowDownSLine, RiArrowUpSLine, RiEyeLine, RiEyeOffLine, RiMapPin2Line, RiPushpinFill, RiPushpinLine, RiSearchLine } from "react-icons/ri";
import ProviderAvatar from "@/components/provider-avatar";
import { PresenceIndicator } from "@/components/team-chat/presence-indicator";
import Button from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import Tooltip from "@/components/ui/tooltip";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { useProviders } from "@/hooks/use-providers";
import { formatStaffName, formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { FirebaseProviderSummary } from "@/types/provider";
import type { ProviderPresenceInfo, TeamConversationDetail, TeamConversationSummary } from "@/types/team-chat";
import { OPTIMISTIC_ID_PREFIX, isOptimisticConversationId, isVisibleConversationsQuery, teamChatQueryKeys, useCreateConversation, useHideConversation, useOnlineStatusMaps, usePinConversation, useTeamConversations, useTeamUnreadCounts, useUnhideConversation, useUnpinConversation } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";

/** Strip entity mentions ({id|label} or {id}) to plain display text for previews */
const MENTION_PREVIEW_REGEX = /\{[a-z]+_[a-zA-Z0-9]+(?:\|([^}]+))?\}/g;
function stripMentions(text: string): string {
  return text.replace(MENTION_PREVIEW_REGEX, (_match, label?: string) => label ? `@${label}` : "");
}
function getConversationDisplayName(conversation: TeamConversationSummary, currentProviderId?: string): string {
  if (conversation.name) return conversation.name;
  if (conversation.type === "DM") {
    // Self-DM: only member is the current user
    if (conversation.members.length === 1 && conversation.members[0].providerId === currentProviderId) {
      return `${conversation.members[0].firstName} ${conversation.members[0].lastName} (you)`;
    }
    const other = conversation.members.find(m => m.providerId !== currentProviderId);
    if (other) return `${other.firstName} ${other.lastName}`;
  }
  return conversation.members.map(m => m.firstName).join(", ");
}
function PresenceStatus(t0) {
  const $ = _c(20);
  const {
    presence,
    locationName
  } = t0;
  if (presence?.customStatusText) {
    let t1;
    if ($[0] !== presence.customStatusEmoji) {
      t1 = presence.customStatusEmoji && <span className="shrink-0">{presence.customStatusEmoji}</span>;
      $[0] = presence.customStatusEmoji;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== presence.customStatusText) {
      t2 = <span className="truncate">{presence.customStatusText}</span>;
      $[2] = presence.customStatusText;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== presence.customStatusText || $[5] !== t1 || $[6] !== t2) {
      t3 = <span className="flex min-w-0 items-center gap-1 text-caption text-foreground-secondary" title={presence.customStatusText}>{t1}{t2}</span>;
      $[4] = presence.customStatusText;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (presence?.status === "WITH_CLIENT" && presence.currentAppointment) {
    let t1;
    if ($[8] !== presence.currentAppointment.endTime) {
      t1 = formatTime(presence.currentAppointment.endTime);
      $[8] = presence.currentAppointment.endTime;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    const endTime = t1;
    let t2;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="shrink-0">🩺</span>;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const t3 = endTime ? `With patient until ${endTime}` : "With patient";
    let t4;
    if ($[11] !== t3) {
      t4 = <span className="flex min-w-0 items-center gap-1 text-caption text-status-warning-text">{t2}<span className="truncate">{t3}</span></span>;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (presence?.status === "BUSY" && presence.currentBlock) {
    let t1;
    if ($[13] !== presence.currentBlock.endTime) {
      t1 = formatTime(presence.currentBlock.endTime);
      $[13] = presence.currentBlock.endTime;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    const endTime_0 = t1;
    const t2 = endTime_0 ? `Busy until ${endTime_0}` : "Busy";
    let t3;
    if ($[15] !== t2) {
      t3 = <span className="flex min-w-0 items-center text-caption text-status-error-text"><span className="truncate">{t2}</span></span>;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  if (locationName) {
    let t1;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <RiMapPin2Line className="h-3 w-3 shrink-0" />;
      $[17] = t1;
    } else {
      t1 = $[17];
    }
    let t2;
    if ($[18] !== locationName) {
      t2 = <span className="flex min-w-0 items-center gap-1 text-caption text-foreground-secondary">{t1}<span className="truncate">{locationName}</span></span>;
      $[18] = locationName;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    return t2;
  }
  return null;
}

/** Flat list item type for keyboard navigation */
type ListItem = {
  type: "self-dm";
} | {
  type: "conversation";
  conversation: TeamConversationSummary;
  hidden?: boolean;
} | {
  type: "team-provider";
  provider: FirebaseProviderSummary;
};
export function ConversationList({
  activeId
}: {
  activeId?: string | null;
}) {
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [onlineExpanded, setOnlineExpanded] = useState(false);
  const [offlineExpanded, setOfflineExpanded] = useState(false);
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  // Whether to render the Online section as a Collapsible. Decided once on
  // first non-loading render based on whether there were any recent chats —
  // we don't want the section to suddenly collapse later when the user starts
  // a new DM and recents transitions from empty to non-empty.
  const [onlineIsCollapsible, setOnlineIsCollapsible] = useState<boolean | null>(null);
  const [pendingProviderId, setPendingProviderId] = useState<string | null>(null);
  const {
    setActiveConversation
  } = useTeamChatDrawerStore();
  const {
    conversations,
    isLoading
  } = useTeamConversations();
  const {
    conversations: hiddenConversations,
    hasNextPage: hasMoreHidden,
    isError: hiddenFetchFailed,
    isFetchingNextPage: isFetchingMoreHidden,
    fetchNextPage: fetchMoreHidden
  } = useTeamConversations(true);
  const {
    data: unreadCounts
  } = useTeamUnreadCounts();
  const {
    onlineStatusMap,
    presenceMap,
    onlineData
  } = useOnlineStatusMaps();
  const {
    currentUser
  } = useUser();
  const {
    mutate: pinConv
  } = usePinConversation();
  const {
    mutate: unpinConv
  } = useUnpinConversation();
  const {
    mutate: hideConv
  } = useHideConversation();
  const {
    mutate: unhideConv
  } = useUnhideConversation();
  const {
    providers,
    providerIdProviderMap
  } = useProviders({
    hasFirebaseUid: true
  });
  const {
    allLocationNameById
  } = useLocations({
    seeAllLocations: true
  });
  const createConversation = useCreateConversation();
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const onlineTriggerRef = useRef<HTMLButtonElement>(null);
  const offlineTriggerRef = useRef<HTMLButtonElement>(null);
  const hiddenTriggerRef = useRef<HTMLButtonElement>(null);

  // After the 200ms collapsible-down animation, scroll the section header to
  // the top of the container so the freshly-revealed rows are visible.
  const scrollSectionHeaderIntoView = (triggerRef: React.RefObject<HTMLButtonElement | null>) => {
    setTimeout(() => {
      triggerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 250);
  };
  const handleOnlineOpenChange = (expanded: boolean) => {
    setOnlineExpanded(expanded);
    if (expanded) scrollSectionHeaderIntoView(onlineTriggerRef);
  };
  const handleOfflineOpenChange = (expanded: boolean) => {
    const container = scrollContainerRef.current;
    const wasNearBottom = container ? container.scrollHeight - container.scrollTop - container.clientHeight < 80 : false;
    setOfflineExpanded(expanded);
    if (expanded && wasNearBottom) {
      scrollSectionHeaderIntoView(offlineTriggerRef);
    }
  };

  // Load every page of hidden chats so the count is accurate and search and
  // unhide reach all of them. Hidden lists are short, so this stays cheap.
  // Stop on error: a failed page leaves hasNextPage true, so without this
  // check the effect would refetch the failing page in a loop.
  useEffect(() => {
    if (hasMoreHidden && !isFetchingMoreHidden && !hiddenFetchFailed) {
      fetchMoreHidden();
    }
  }, [hasMoreHidden, isFetchingMoreHidden, hiddenFetchFailed, fetchMoreHidden]);
  const handleHiddenOpenChange = (expanded: boolean) => {
    setHiddenExpanded(expanded);
    if (expanded) scrollSectionHeaderIntoView(hiddenTriggerRef);
  };
  const onlineProviderIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of onlineData?.providers ?? []) {
      ids.add(p.providerId);
    }
    return ids;
  }, [onlineData]);
  const filtered = useMemo(() => {
    if (!search) return conversations;
    return conversations.filter(c => getConversationDisplayName(c, currentUser?.id).toLowerCase().includes(search.toLowerCase()));
  }, [conversations, search, currentUser?.id]);

  // Find existing self-DM (notes to self) — a DM where the only member is current user
  const selfDm = useMemo(() => {
    if (!currentUser?.id) return null;
    return conversations.find(c => c.type === "DM" && c.members.length === 1 && c.members[0].providerId === currentUser.id) ?? null;
  }, [conversations, currentUser?.id]);
  const existingDmProviderIds = useMemo(() => {
    const ids = new Set<string>();
    for (const conv of conversations) {
      if (conv.type === "DM") {
        for (const m of conv.members) {
          if (m.providerId !== currentUser?.id) ids.add(m.providerId);
        }
      }
    }
    return ids;
  }, [conversations, currentUser?.id]);
  const {
    pinnedRows,
    recentRows,
    onlineProviderRows,
    offlineProviderRows,
    hiddenRows
  } = useMemo(() => {
    const compareConversations = (a: ListItem, b: ListItem) => {
      if (a.type !== "conversation" || b.type !== "conversation") return 0;
      const ca = a.conversation;
      const cb = b.conversation;
      const ua = unreadCounts?.[ca.id] ?? 0;
      const ub = unreadCounts?.[cb.id] ?? 0;
      const tierA = ua > 0 ? 0 : ca.lastMessageAt ? 1 : 2;
      const tierB = ub > 0 ? 0 : cb.lastMessageAt ? 1 : 2;
      if (tierA !== tierB) return tierA - tierB;
      const tA = ca.lastMessageAt ? new Date(ca.lastMessageAt).getTime() : 0;
      const tB = cb.lastMessageAt ? new Date(cb.lastMessageAt).getTime() : 0;
      if (tA !== tB) return tB - tA;
      return getConversationDisplayName(ca, currentUser?.id).localeCompare(getConversationDisplayName(cb, currentUser?.id));
    };
    const compareProviders = (a: ListItem, b: ListItem) => {
      if (a.type !== "team-provider" || b.type !== "team-provider") return 0;
      const aName = `${a.provider.firstName ?? ""} ${a.provider.lastName ?? ""}`;
      const bName = `${b.provider.firstName ?? ""} ${b.provider.lastName ?? ""}`;
      return aName.localeCompare(bName);
    };
    const pinned: ListItem[] = [];
    const recent: ListItem[] = [];
    const onlineP: ListItem[] = [];
    const offlineP: ListItem[] = [];
    const hiddenR: ListItem[] = [];
    for (const c of filtered) {
      if (c.id === selfDm?.id) continue;
      const row: ListItem = {
        type: "conversation",
        conversation: c
      };
      if (c.pinned) pinned.push(row);else recent.push(row);
    }
    const searchLower = search.toLowerCase();
    for (const c of hiddenConversations) {
      if (search && !getConversationDisplayName(c, currentUser?.id).toLowerCase().includes(searchLower)) {
        continue;
      }
      hiddenR.push({
        type: "conversation",
        conversation: c,
        hidden: true
      });
    }
    for (const p of providers) {
      if (p.id === currentUser?.id) continue;
      if (p.isArchived) continue;
      if (existingDmProviderIds.has(p.id)) continue;
      if (search) {
        const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.toLowerCase();
        if (!fullName.includes(searchLower)) continue;
      }
      const row: ListItem = {
        type: "team-provider",
        provider: p
      };
      if (onlineProviderIds.has(p.id)) onlineP.push(row);else offlineP.push(row);
    }
    pinned.sort(compareConversations);
    recent.sort(compareConversations);
    onlineP.sort(compareProviders);
    offlineP.sort(compareProviders);
    hiddenR.sort(compareConversations);
    return {
      pinnedRows: pinned,
      recentRows: recent,
      onlineProviderRows: onlineP,
      offlineProviderRows: offlineP,
      hiddenRows: hiddenR
    };
  }, [filtered, hiddenConversations, selfDm?.id, providers, currentUser?.id, search, existingDmProviderIds, onlineProviderIds, unreadCounts]);

  // Build flat list of all selectable items for keyboard navigation
  const showSelfDm = !search || "you".includes(search.toLowerCase());
  // Hidden chats stay tucked away until asked for, but a search that matches
  // one should surface it — otherwise a hidden chat is unreachable.
  const showHiddenRows = hiddenExpanded || !!search && hiddenRows.length > 0;
  const flatItems = useMemo(() => {
    const items: ListItem[] = [];
    if (showSelfDm) items.push({
      type: "self-dm"
    });
    items.push(...pinnedRows);
    items.push(...recentRows);
    // Online is hidden only when we've decided it should be collapsible AND
    // it's currently collapsed. Otherwise rows are always navigable.
    if (!onlineIsCollapsible || onlineExpanded) {
      items.push(...onlineProviderRows);
    }
    if (offlineExpanded) items.push(...offlineProviderRows);
    if (showHiddenRows) items.push(...hiddenRows);
    return items;
  }, [showSelfDm, pinnedRows, recentRows, onlineProviderRows, offlineProviderRows, hiddenRows, onlineIsCollapsible, onlineExpanded, offlineExpanded, showHiddenRows]);

  // Reset focused index when flatItems list changes (e.g., new conversations arriving)
  const prevFlatItemsLenRef = useRef(flatItems.length);
  useEffect(() => {
    if (flatItems.length !== prevFlatItemsLenRef.current && search) {
      setFocusedIndex(flatItems.length > 0 ? 0 : -1);
    }
    prevFlatItemsLenRef.current = flatItems.length;
  }, [flatItems.length, search]);

  // Lock in the Online section's collapsible-vs-flat mode the first time we
  // have data, then leave it alone for the lifetime of the component.
  useEffect(() => {
    if (onlineIsCollapsible !== null) return;
    if (isLoading) return;
    setOnlineIsCollapsible(recentRows.length > 0);
  }, [isLoading, recentRows.length, onlineIsCollapsible]);

  // After an optimistic create lands a row in Recent, scroll it into view so
  // the user actually sees it move out of Online/Offline.
  const [pendingScrollToId, setPendingScrollToId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingScrollToId) return;
    const el = scrollContainerRef.current?.querySelector<HTMLElement>(`[data-conversation-id="${pendingScrollToId}"]`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
      setPendingScrollToId(null);
    }
  }, [pendingScrollToId, recentRows]);
  const optimisticallyOpenDm = (pendingKey: string, other: {
    providerId: string;
    firstName: string;
    lastName: string;
  } | null) => {
    const tempId = `${OPTIMISTIC_ID_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: TeamConversationDetail = {
      id: tempId,
      type: "DM",
      name: null,
      members: other ? [{
        id: `${tempId}_other`,
        providerId: other.providerId,
        firstName: other.firstName,
        lastName: other.lastName,
        profileImage: null,
        role: "MEMBER"
      }] : currentUser ? [{
        id: `${tempId}_self`,
        providerId: currentUser.id,
        firstName: currentUser.firstName ?? "",
        lastName: currentUser.lastName ?? "",
        profileImage: null,
        role: "MEMBER"
      }] : [],
      lastMessageAt: null,
      lastMessagePreview: null,
      unreadCount: 0,
      pinned: false,
      createdDate: new Date().toISOString(),
      description: null,
      topic: null,
      creatorId: currentUser?.id ?? null
    };
    queryClient.setQueryData(teamChatQueryKeys.conversationDetail(tempId), optimistic);
    queryClient.setQueryData(teamChatQueryKeys.messages(tempId), {
      pages: [{
        data: [],
        nextCursor: null
      }],
      pageParams: [undefined]
    });
    queryClient.setQueriesData({
      queryKey: teamChatQueryKeys.conversations(),
      predicate: isVisibleConversationsQuery
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (old: any) => {
      if (!old?.pages?.length) return old;
      return {
        ...old,
        pages: [{
          ...old.pages[0],
          data: [optimistic, ...(old.pages[0].data ?? [])]
        }, ...old.pages.slice(1)]
      };
    });
    setActiveConversation(tempId);
    setPendingProviderId(pendingKey);
    setPendingScrollToId(tempId);
    createConversation.mutate({
      type: "DM",
      memberIds: other ? [other.providerId] : []
    }, {
      onSuccess: real => {
        if (!real?.id) return;
        // Swap temp → real in the conversations list (in case the row is
        // visible) and switch the active conversation to the real ID.
        queryClient.setQueriesData({
          queryKey: teamChatQueryKeys.conversations(),
          predicate: isVisibleConversationsQuery
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.pages?.length) return old;
          return {
            ...old,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: old.pages.map((page: any) => ({
              ...page,
              data: (page.data ?? []).map((c: TeamConversationSummary) => c.id === tempId ? real : c)
            }))
          };
        });
        // Detail + messages caches for the real ID are primed by
        // useCreateConversation.onSuccess.
        setActiveConversation(real.id);
        queryClient.removeQueries({
          queryKey: teamChatQueryKeys.conversationDetail(tempId)
        });
        queryClient.removeQueries({
          queryKey: teamChatQueryKeys.messages(tempId)
        });
      },
      onError: () => {
        // Roll back the optimistic insert.
        queryClient.setQueriesData({
          queryKey: teamChatQueryKeys.conversations(),
          predicate: isVisibleConversationsQuery
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.pages?.length) return old;
          return {
            ...old,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: old.pages.map((page: any) => ({
              ...page,
              data: (page.data ?? []).filter((c: TeamConversationSummary) => c.id !== tempId)
            }))
          };
        });
        queryClient.removeQueries({
          queryKey: teamChatQueryKeys.conversationDetail(tempId)
        });
        queryClient.removeQueries({
          queryKey: teamChatQueryKeys.messages(tempId)
        });
        setActiveConversation(null);
      },
      onSettled: () => setPendingProviderId(null)
    });
  };
  const handleCreateDm = (provider: FirebaseProviderSummary) => {
    optimisticallyOpenDm(provider.id, {
      providerId: provider.id,
      firstName: provider.firstName ?? "",
      lastName: provider.lastName ?? ""
    });
  };
  const handleOpenSelfDm = () => {
    if (selfDm) {
      setActiveConversation(selfDm.id);
      return;
    }
    optimisticallyOpenDm("self", null);
  };
  const selectItem = useCallback((item: ListItem) => {
    if (item.type === "self-dm") {
      handleOpenSelfDm();
    } else if (item.type === "conversation") {
      setActiveConversation(item.conversation.id);
    } else if (item.type === "team-provider") {
      handleCreateDm(item.provider);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [selfDm, setActiveConversation, createConversation]);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (flatItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev < flatItems.length - 1 ? prev + 1 : 0;
        itemRefs.current.get(next)?.scrollIntoView({
          block: "nearest"
        });
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev > 0 ? prev - 1 : flatItems.length - 1;
        itemRefs.current.get(next)?.scrollIntoView({
          block: "nearest"
        });
        return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < flatItems.length) {
        selectItem(flatItems[focusedIndex]);
      }
    }
  }, [flatItems, focusedIndex, selectItem]);

  // Each row's position in flatItems, which keyboard focus indexes into.
  // Rows in a collapsed section are left out: their JSX is still built, so a
  // running counter would drift past them and misnumber every later section.
  const flatIndexByItem = new Map(flatItems.map((item, i) => [item, i]));
  return <div className="flex h-full flex-col" onKeyDown={handleKeyDown}>
      <div className="px-3 pb-2 pt-2">
        <Input ref={searchInputRef} icon={RiSearchLine} placeholder="Search conversations..." value={search} onChange={e => {
        const value = e.target.value;
        setSearch(value);
        setFocusedIndex(value && flatItems.length > 0 ? 0 : -1);
      }} />
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-2 pb-3">
        {isLoading ? <div className="space-y-1 p-2">
            {Array.from({
          length: 6
        }).map((_, i) => <div key={i} className="flex items-center gap-3 rounded-lg p-2">
                <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>)}
          </div> : <>
            {showSelfDm && (() => {
          const selfDmIdx = 0;
          return <button ref={el => {
            if (el) itemRefs.current.set(selfDmIdx, el);
          }} onClick={handleOpenSelfDm} disabled={pendingProviderId === "self"} className={cx("group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-hover disabled:opacity-50", activeId && selfDm?.id === activeId && "bg-hover", focusedIndex === selfDmIdx && "bg-hover")}>
                    <div className="relative shrink-0">
                      <PresenceIndicator status={currentUser?.id && onlineStatusMap.has(currentUser.id) ? onlineStatusMap.get(currentUser.id) : undefined}>
                        <ProviderAvatar providerId={currentUser?.id ?? ""} size={36} />
                      </PresenceIndicator>
                      {currentUser?.id && presenceMap.get(currentUser.id)?.customStatusEmoji && <Tooltip content={presenceMap.get(currentUser.id)?.customStatusText} side="bottom" triggerAsChild>
                            <span className="absolute -top-1 -right-1 text-caption leading-none">
                              {presenceMap.get(currentUser.id)?.customStatusEmoji}
                            </span>
                          </Tooltip>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Label className="block truncate pb-0">
                        {currentUser ? <span>{formatStaffName(currentUser)}</span> : <span>{"You"}</span>}{" "}
                        <span className="text-foreground-muted">(you)</span>
                      </Label>
                      {(selfDm?.lastMessagePreview || currentUser?.id && presenceMap.get(currentUser.id)?.customStatusText) && <p className="truncate text-caption text-foreground-secondary">
                          {selfDm?.lastMessagePreview ? stripMentions(selfDm.lastMessagePreview) : `${presenceMap.get(currentUser!.id)?.customStatusEmoji ?? ""} ${presenceMap.get(currentUser!.id)?.customStatusText}`.trim()}
                        </p>}
                    </div>
                  </button>;
        })()}

            {pinnedRows.length > 0 && <>
                <div className="px-3 pb-1 pt-3 text-tiny font-semibold uppercase tracking-wider text-foreground-secondary">
                  Pinned
                </div>
                {pinnedRows.map(row => {
            const idx = flatIndexByItem.get(row) ?? -1;
            return renderRow(row, idx);
          })}
              </>}

            {recentRows.length > 0 && <>
                <div className="px-3 pb-1 pt-3 text-tiny font-semibold uppercase tracking-wider text-foreground-secondary">
                  Recent
                </div>
                {recentRows.map(row => {
            const idx = flatIndexByItem.get(row) ?? -1;
            return renderRow(row, idx);
          })}
              </>}

            {onlineProviderRows.length > 0 && (!onlineIsCollapsible ? <>
                  <div className="px-3 pb-1 pt-3 text-tiny font-semibold uppercase tracking-wider text-foreground-secondary">
                    Online
                  </div>
                  {onlineProviderRows.map(row => {
            const idx = flatIndexByItem.get(row) ?? -1;
            return renderRow(row, idx);
          })}
                </> : <Collapsible open={onlineExpanded} onOpenChange={handleOnlineOpenChange}>
                  <CollapsibleTrigger asChild>
                    <button ref={onlineTriggerRef} type="button" className="flex w-full items-center justify-between px-3 pb-1 pt-3 text-left text-tiny font-semibold uppercase tracking-wider text-foreground-secondary transition-colors hover:text-foreground">
                      <span>Online ({onlineProviderRows.length})</span>
                      {onlineExpanded ? <RiArrowUpSLine className="h-4 w-4" /> : <RiArrowDownSLine className="h-4 w-4" />}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {onlineProviderRows.map(row => {
              const idx = flatIndexByItem.get(row) ?? -1;
              return renderRow(row, idx);
            })}
                  </CollapsibleContent>
                </Collapsible>)}

            {offlineProviderRows.length > 0 && <Collapsible open={offlineExpanded} onOpenChange={handleOfflineOpenChange}>
                <CollapsibleTrigger asChild>
                  <button ref={offlineTriggerRef} type="button" className="flex w-full items-center justify-between px-3 pb-1 pt-3 text-left text-tiny font-semibold uppercase tracking-wider text-foreground-secondary transition-colors hover:text-foreground">
                    <span>Offline ({offlineProviderRows.length})</span>
                    {offlineExpanded ? <RiArrowUpSLine className="h-4 w-4" /> : <RiArrowDownSLine className="h-4 w-4" />}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {offlineProviderRows.map(row => {
              const idx = flatIndexByItem.get(row) ?? -1;
              return renderRow(row, idx);
            })}
                </CollapsibleContent>
              </Collapsible>}

            {hiddenRows.length > 0 && <Collapsible open={showHiddenRows} onOpenChange={handleHiddenOpenChange}>
                <CollapsibleTrigger asChild>
                  <button ref={hiddenTriggerRef} type="button" className="flex w-full items-center justify-between px-3 pb-1 pt-3 text-left text-tiny font-semibold uppercase tracking-wider text-foreground-secondary transition-colors hover:text-foreground">
                    <span>Hidden ({hiddenRows.length})</span>
                    {showHiddenRows ? <RiArrowUpSLine className="h-4 w-4" /> : <RiArrowDownSLine className="h-4 w-4" />}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {hiddenRows.map(row => {
              const idx = flatIndexByItem.get(row) ?? -1;
              return renderRow(row, idx);
            })}
                </CollapsibleContent>
              </Collapsible>}
          </>}
      </div>
    </div>;
  function renderRow(row: ListItem, idx: number) {
    if (row.type === "conversation") {
      return renderConversationItem(row.conversation, idx, row.hidden);
    }
    if (row.type === "team-provider") {
      return renderProviderItem(row.provider, idx);
    }
    return null;
  }
  function renderProviderItem(provider: FirebaseProviderSummary, idx: number) {
    const isOnline = onlineProviderIds.has(provider.id);
    const status = onlineStatusMap.get(provider.id);
    return <button key={provider.id} ref={el => {
      if (el && idx >= 0) itemRefs.current.set(idx, el);
    }} onClick={() => handleCreateDm(provider)} disabled={pendingProviderId === provider.id} className={cx("flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-hover disabled:opacity-50", focusedIndex === idx && "bg-hover")}>
        <div className="relative shrink-0">
          <PresenceIndicator status={isOnline ? status : undefined}>
            <ProviderAvatar providerId={provider.id} size={36} />
          </PresenceIndicator>
          {presenceMap.get(provider.id)?.customStatusEmoji && <Tooltip content={presenceMap.get(provider.id)?.customStatusText} side="bottom" triggerAsChild>
              <span className="absolute -top-1 -right-1 text-caption leading-none">
                {presenceMap.get(provider.id)?.customStatusEmoji}
              </span>
            </Tooltip>}
        </div>
        <div className="min-w-0 flex-1">
          <Label className="block truncate pb-0">
            {formatStaffName(provider)}
          </Label>
          <PresenceStatus presence={presenceMap.get(provider.id)} locationName={provider.primaryLocationId ? allLocationNameById[provider.primaryLocationId] : null} />
        </div>
      </button>;
  }
  function renderConversationItem(conversation: TeamConversationSummary, idx: number, hidden = false) {
    const unread = unreadCounts?.[conversation.id] ?? 0;
    const otherMember = conversation.type === "DM" ? conversation.members.find(m => m.providerId !== currentUser?.id) : undefined;
    const otherProviderId = otherMember?.providerId ?? (conversation.type === "DM" ? conversation.members[0]?.providerId : undefined) ?? "";
    const isOnline = otherProviderId && onlineProviderIds.has(otherProviderId);
    const status = otherProviderId ? onlineStatusMap.get(otherProviderId) : undefined;

    // The row actions sit beside the clickable row, not inside it, so the
    // row's role="button" never contains other buttons.
    return <div key={conversation.id} className="group relative">
        <div role="button" tabIndex={0} data-conversation-id={conversation.id} ref={el => {
        if (el && idx >= 0) itemRefs.current.set(idx, el);
      }} onClick={() => setActiveConversation(conversation.id)} onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setActiveConversation(conversation.id);
        }
      }} className={cx("flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-hover", activeId === conversation.id ? "bg-hover" : unread > 0 && "bg-background-secondary", focusedIndex === idx && "bg-hover")}>
          <div className="relative shrink-0">
            {conversation.type === "DM" ? <>
                <PresenceIndicator status={isOnline ? status : undefined}>
                  <ProviderAvatar providerId={otherProviderId} size={36} />
                </PresenceIndicator>
                {otherProviderId && presenceMap.get(otherProviderId)?.customStatusEmoji && <Tooltip content={presenceMap.get(otherProviderId)?.customStatusText} side="bottom" triggerAsChild>
                      <span className="absolute -top-1 -right-1 text-caption leading-none">
                        {presenceMap.get(otherProviderId)?.customStatusEmoji}
                      </span>
                    </Tooltip>}
              </> : <div className="relative h-9 w-9">
                {conversation.members.filter(m => m.providerId !== currentUser?.id).slice(0, 2).map((member, i) => <div key={member.providerId} className={cx("absolute rounded-full ring-2 ring-background", i === 0 ? "bottom-0 right-0 z-[1]" : "left-0 top-0")}>
                      <ProviderAvatar providerId={member.providerId} size={24} />
                    </div>)}
              </div>}
          </div>
          <div className="min-w-0 flex-1">
            <Label className={cx("block truncate pb-0", unread > 0 && "font-semibold")}>
              {getConversationDisplayName(conversation, currentUser?.id)}
            </Label>
            {conversation.type === "DM" && otherProviderId && <div className="min-w-0">
                <PresenceStatus presence={presenceMap.get(otherProviderId)} locationName={(() => {
              const p = providerIdProviderMap.get(otherProviderId);
              return p?.primaryLocationId ? allLocationNameById[p.primaryLocationId] : null;
            })()} />
              </div>}
            <div className="flex items-center justify-between">
              <p className={cx("truncate text-caption text-foreground-secondary", unread > 0 && "font-medium")}>
                {conversation.lastMessagePreview ? stripMentions(conversation.lastMessagePreview) : "No messages yet"}
              </p>
              {unread > 0 && <span className="ml-2 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-caption-strong text-primary-foreground">
                  {unread > 99 ? "99+" : unread}
                </span>}
            </div>
          </div>
        </div>
        <ButtonGroup className={cx("absolute right-2 top-2 rounded-md border border-border bg-background shadow-sm", isOptimisticConversationId(conversation.id) ? "hidden" : conversation.pinned && !hidden ? "flex" : "hidden group-hover:flex")}>
          {hidden ? <Button variant="ghost" size="icon-sm" className="size-7 rounded-md text-foreground-secondary" tooltip="Unhide conversation" aria-label="Unhide conversation" onClick={e => {
          e.stopPropagation();
          unhideConv(conversation.id);
        }}>
              <RiEyeLine className="h-4 w-4" />
            </Button> : <>
              <Button variant="ghost" size="icon-sm" className="size-7 rounded-md text-foreground-secondary" tooltip={conversation.pinned ? "Unpin conversation" : "Pin conversation"} aria-label={conversation.pinned ? "Unpin conversation" : "Pin conversation"} onClick={e => {
            e.stopPropagation();
            if (conversation.pinned) {
              unpinConv(conversation.id);
            } else {
              pinConv(conversation.id);
            }
          }}>
                {conversation.pinned ? <RiPushpinFill className="h-4 w-4" /> : <RiPushpinLine className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon-sm" className="size-7 rounded-md text-foreground-secondary" tooltip="Remove from recent" aria-label="Remove from recent" onClick={e => {
            e.stopPropagation();
            if (conversation.id === activeId) {
              setActiveConversation(null);
            }
            hideConv(conversation.id);
          }}>
                <RiEyeOffLine className="h-4 w-4" />
              </Button>
            </>}
        </ButtonGroup>
      </div>;
  }
}